import React from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';

const dataServices = [
  {
    icon: '/pictures/improved_quality_841c5f0827.svg',
    title: 'Business Intelligence & Dashboards',
    desc: 'Transform raw data into interactive dashboards and reports. Real-time BI solutions built on Power BI, Tableau, or custom-built platforms that empower every stakeholder.',
  },
  {
    icon: '/pictures/agile_development_process_11fe77acf4.svg',
    title: 'Data Pipeline Engineering',
    desc: 'Design and build robust ETL/ELT pipelines that move, transform, and load data reliably. From batch processing to real-time streaming with Apache Kafka and Spark.',
  },
  {
    icon: '/pictures/stack_248277a23c.svg',
    title: 'Data Warehouse & Lake Architecture',
    desc: 'Architect centralized data stores that consolidate your organization\'s data assets. Design modern data lakes and warehouses on Snowflake, BigQuery, or Redshift.',
  },
  {
    icon: '/pictures/innovation_561a49ed62.svg',
    title: 'Machine Learning & AI',
    desc: 'Build predictive models, recommendation engines, and classification systems. From data preparation to model deployment and monitoring in production.',
  },
  {
    icon: '/pictures/shield_check_7246d46e1b.svg',
    title: 'Data Quality & Governance',
    desc: 'Establish data quality frameworks, lineage tracking, and governance policies. Ensure your data is accurate, consistent, and trustworthy across the organization.',
  },
  {
    icon: '/pictures/global_scale_94d2c82201.svg',
    title: 'Security Analytics',
    desc: 'Apply data analytics to cybersecurity: anomaly detection, threat intelligence correlation, user behavior analytics (UBA), and SIEM data enrichment.',
  },
];

const techStack = [
  'Python', 'Apache Spark', 'Apache Kafka', 'dbt',
  'Snowflake', 'BigQuery', 'Redshift', 'Power BI',
  'Tableau', 'TensorFlow', 'PyTorch', 'Airflow',
];

export default function DataAnalytics() {
  return (
    <PageLayout>
      <SEOHead
        title="Data Analytics & Business Intelligence"
        description="Transform raw data into actionable insights. Data engineering, ETL pipelines, BI dashboards, predictive analytics, and machine learning solutions."
        keywords="data analytics, business intelligence, data engineering, ETL, data pipelines, machine learning, predictive analytics, Power BI, Tableau"
        url="/services/data-analytics"
      />
      {/* Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Services</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Data Analytics</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Data Analytics
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            Transform raw data into actionable insights with advanced analytics, BI dashboards, and machine learning models that drive smarter decisions.
          </p>
        </div>
      </div>

      {/* Intro */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>Turn Data Into Your Competitive Advantage</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '20px' }}>
              Data is the new oil — but only if you can refine it. Most organizations are drowning in data yet starved for insight. DISION Tech bridges that gap with end-to-end data analytics services that span engineering, analysis, and machine learning.
            </p>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              Whether you need a modern data platform, real-time dashboards, or predictive models, our data engineers and scientists work alongside your team to build solutions that create measurable business impact.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/intro_bg1_2a870dcff5.png" alt="Data Analytics" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Services */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Our Data Analytics Services</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>Full-spectrum data capabilities from engineering to intelligence.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {dataServices.map((s, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', padding: '11px' }}>
                  <img src={s.icon} alt={s.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack tags */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Technologies We Use</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '40px', maxWidth: '480px' }}>Our data engineering stack is modern, battle-tested, and cloud-native.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {techStack.map((tech, i) => (
              <span key={i} style={{ padding: '8px 20px', borderRadius: '20px', background: '#F0F9FF', color: '#0099CC', fontSize: '14px', fontWeight: '600', border: '1px solid #BAE6FD' }}>{tech}</span>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
